import React, {useState} from "react";
import Tr from "translations/Translations";
import PropTypes from "prop-types";
import Dialog from "components/Dialog";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import Loading from "components/Loading";
import GridBlock from "components/GridBlock";
import {makeStyles} from "@material-ui/core";
import Form from "components/Form";
import useId from "hooks/useId";
import VS from "constants/ValidationShema";
import DateTimePicker from "components/DateTimePicker";
import TypographyWithIcon from "components/TypographyWithIcon";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {connect} from "react-redux";
import AuctionService from "services/AuctionService";
import {AuctionRule} from "constants/AuctionRule";
import TextInput from "components/TextInput";
import INPUT_TYPE from "constants/INPUT_TYPE";
import moment from "libs/moment";
import isResponseValid from "libs/isResponseValid";
import {AuctionStatus} from "constants/AuctionStatus";
import OfferChoiceService from "services/OfferChoiceService";

const useStyle = makeStyles(theme => ({
    title: {
        marginTop: 0
    },
    italic: {
        fontStyle: 'italic'
    },
    bold: {
        fontWeight: theme.typography.fontWeightBold
    },
    formElement: {
        width: 400
    },
}));


const EditDatesAuctionPopup = (
    {
        demandId,
        offerChoiceRow,
        onCancel,
        onSuccess,
        roundId,
        auction,
        formSubmit,
    }) => {

    const classes = useStyle();

    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const formId = useId();
    const handleSubmit = () => formSubmit[formId]();

    const onSubmit = (values) => {
        setError(null);
        setWorking(true);
        AuctionService.editDates(demandId, roundId, values).then((resp) => {
            if(isResponseValid(resp, setError)) {
                OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
                onSuccess();
            } else {
                setWorking(false);
            }
        }).catch(() => {
            setError(Tr('unknown_error_message'));
            setWorking(false);
        })
    }

    const handleEndDate = (formikProps, value) => {
        formikProps.setFieldValue('endDate', value);
        formikProps.setFieldTouched('endDate', true);
    }

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('cancel'), onCancel, 'outlined', working));
        buttons.push(createButton(Tr('auction_update_dates_button'), handleSubmit, undefined, working));
        return buttons;
    }


    const validationSchema = VS.lazy((value) => {
        const shape = {
        }
        if(value.status !== AuctionStatus.PAUSED) {
            shape.startDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
        }

        if (AuctionRule.JAPANESE !== value.rule) {
            shape.endDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
                .when('startDate', (startDate, schema) => {
                    return schema.test('EndDateLessStartDate', Tr("EndDateLessStartDate"), (endDate) => {
                        if (startDate && endDate) {
                            return endDate.isAfter(startDate);
                        }
                        return false;
                    })
                })
        } else {
            shape.decisionTime = VS.integerMax(99999)
        }
        return VS.object(shape);
    })


    return (
        <Dialog
            open
            dialogActions={<MultiButton buttons={getButtons()}/>}
            title={Tr('auction_create_popup_title')}
            maxWidth='sm'
        >
            <GridBlock
                column
            >

                {working && <Loading/>}
                {!!error && <TypographyWithIcon Icon={ErrorOutlineIcon} color='error'>{error}</TypographyWithIcon>}

                <Form
                    initialValues={{
                        ...auction,
                        startDate: moment(auction.startDate),
                        endDate: auction.endDate ? moment(auction.endDate) : null
                    }}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    id={formId}
                >
                    {(formikProps) => (
                        <GridBlock
                            column
                        >
                            <DateTimePicker
                                className={classes.formElement}
                                id='startDate'
                                label={Tr('auction_start_date')}
                                formikProps={formikProps}
                                disabled={working || auction.status === AuctionStatus.PAUSED}
                                onChange={(value) => handleEndDate(formikProps, value)}
                                minDate={new Date()}
                            />

                            {AuctionRule.JAPANESE === formikProps.values.rule ?
                                <TextInput
                                    className={classes.formElement}
                                    disabled={working}
                                    label={Tr('auction_decision_time')}
                                    id='decisionTime'
                                    required
                                    formikProps={formikProps}
                                    type={INPUT_TYPE.INTEGER_POSITIVE_AMOUNT}
                                />
                                :
                                <DateTimePicker
                                    className={classes.formElement}
                                    id='endDate'
                                    label={Tr('auction_end_date')}
                                    formikProps={formikProps}
                                    disabled={working}
                                    minDate={new Date()}
                                />
                            }
                        </GridBlock>
                    )}
                </Form>
            </GridBlock>
        </Dialog>)
}

EditDatesAuctionPopup.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    offerChoiceRow: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    roundId: PropTypes.number.isRequired,
    auction: PropTypes.object.isRequired
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(EditDatesAuctionPopup)